import type { IconProps } from '@tabler/icons-react';
import {
  IconActivity,
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconArrowUpRight,
  IconBell,
  IconBinaryTree,
  IconBolt,
  IconBoxMultiple,
  IconBrandDiscord,
  IconBuilding,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconClock,
  IconCode,
  IconCopy,
  IconCpu,
  IconDatabase,
  IconDeviceGamepad2,
  IconDiscount2,
  IconDroplet,
  IconDotsVertical,
  IconDownload,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFile,
  IconFilter,
  IconFingerprint,
  IconFolder,
  IconGauge,
  IconKey,
  IconLayersLinked,
  IconLayoutDashboard,
  IconLink,
  IconLoader2,
  IconLock,
  IconLockOpen,
  IconLogout,
  IconMail,
  IconMenu2,
  IconMoon,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconRocket,
  IconSearch,
  IconSettings,
  IconShield,
  IconShieldCheck,
  IconShieldLock,
  IconShoppingCart,
  IconSortAscending,
  IconSparkles,
  IconStack2,
  IconSun,
  IconTerminal2,
  IconTicket,
  IconTrash,
  IconTrendingUp,
  IconUpload,
  IconUser,
  IconUsers,
  IconWorld,
  IconX,
} from '@tabler/icons-react';

export const tablerIcons = {
  activity: IconActivity,
  alertCircle: IconAlertCircle,
  alertTriangle: IconAlertTriangle,
  arrowLeft: IconArrowLeft,
  arrowRight: IconArrowRight,
  arrowUpRight: IconArrowUpRight,
  bell: IconBell,
  binaryTree: IconBinaryTree,
  bolt: IconBolt,
  boxMultiple: IconBoxMultiple,
  brandDiscord: IconBrandDiscord,
  building: IconBuilding,
  chart: IconChartBar,
  check: IconCheck,
  chevronDown: IconChevronDown,
  chevronLeft: IconChevronLeft,
  chevronRight: IconChevronRight,
  chevronUp: IconChevronUp,
  clock: IconClock,
  code: IconCode,
  copy: IconCopy,
  cpu: IconCpu,
  database: IconDatabase,
  deviceGamepad: IconDeviceGamepad2,
  discount: IconDiscount2,
  droplet: IconDroplet,
  dotsVertical: IconDotsVertical,
  download: IconDownload,
  external: IconExternalLink,
  eye: IconEye,
  eyeOff: IconEyeOff,
  file: IconFile,
  filter: IconFilter,
  fingerprint: IconFingerprint,
  folder: IconFolder,
  gauge: IconGauge,
  key: IconKey,
  layers: IconLayersLinked,
  link: IconLink,
  loader: IconLoader2,
  lock: IconLock,
  lockOpen: IconLockOpen,
  logout: IconLogout,
  mail: IconMail,
  menu: IconMenu2,
  moon: IconMoon,
  nav: IconLayoutDashboard,
  pencil: IconPencil,
  plus: IconPlus,
  refresh: IconRefresh,
  rocket: IconRocket,
  search: IconSearch,
  settings: IconSettings,
  shield: IconShield,
  shieldCheck: IconShieldCheck,
  shieldLock: IconShieldLock,
  shopping: IconShoppingCart,
  sort: IconSortAscending,
  sparkles: IconSparkles,
  stack: IconStack2,
  sun: IconSun,
  terminal: IconTerminal2,
  ticket: IconTicket,
  trash: IconTrash,
  trending: IconTrendingUp,
  upload: IconUpload,
  user: IconUser,
  users: IconUsers,
  world: IconWorld,
  x: IconX,
} as const;

export type IconName = keyof typeof tablerIcons;

export const iconNames = Object.keys(tablerIcons) as IconName[];

export const iconSizes = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
} as const;

export type IconSize = keyof typeof iconSizes;

export interface LuaIconProps extends Omit<IconProps, 'size'> {
  name: IconName;
  size?: IconSize | number;
  strokeWidth?: number;
  label?: string;
}

export function Icon({ name, size = 'md', strokeWidth = 1.75, label, ...rest }: LuaIconProps) {
  const Component = tablerIcons[name];
  const resolved = typeof size === 'number' ? size : iconSizes[size];
  return (
    <Component
      size={resolved}
      stroke={strokeWidth}
      aria-label={label}
      role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : true}
      focusable={false}
      {...rest}
    />
  );
}

export {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconKey,
  IconLoader2,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSettings,
  IconTrash,
  IconUser,
  IconUsers,
  IconX,
};
export type { IconProps };
